/**
 * The one navigation bar for every signed-in page.
 *
 * Each page used to build its own, and they had all drifted apart: the
 * dashboard offered Locations, Scan and Plan but not Reports; the list page
 * offered Dashboard and Scan; the reports page offered Locations and nothing
 * else; and /team had no navigation at all, so it was reachable only by typing
 * the URL and offered no way back out. An admin could not find team management,
 * and a manager could not find the reports that are the reason they were given
 * the account.
 *
 * Links are gated by role, taken from ROLE_PERMISSIONS in middleware/rbac.ts
 * rather than from an assumption about what each role "probably" does:
 *
 *   - everyone   dashboard, extinguishers, locations, scan, settings, reports.
 *                An inspector holds REPORTS_VIEW, and the reports page reads
 *                only /api/compliance, /api/extinguishers and /api/inspections,
 *                all of which an inspector can read. Only the PDF downloads
 *                need REPORTS_GENERATE, and the page hides those.
 *   - manager    also team. A manager holds USERS_VIEW ("can view team
 *                members") but not USERS_INVITE, and the team page already
 *                hides the invite and remove controls accordingly. Hiding the
 *                link entirely denied them a page they are permitted to see.
 *   - admin      also plan. Billing is admin-only.
 */

import { api } from './api.js';
import { visibleNavItems, NAV_ITEMS, type NavItem } from './nav-items.js';

export { visibleNavItems, NAV_ITEMS, type NavItem };
import { router } from './router.js';

/**
 * Builds the nav element. `currentPath` marks the active link so the user can
 * see where they are; it is still clickable, because a link that silently does
 * nothing is worse than a redundant one.
 */
export function buildAppNav(
  create: (tag: string, props?: Record<string, unknown>, text?: string) => HTMLElement,
  currentPath: string,
  onLogout: () => void
): HTMLElement {
  const role = api.currentUser()?.role ?? null;
  const nav = create('nav', { className: 'nav' });
  nav.setAttribute('aria-label', 'Main');

  for (const item of visibleNavItems(role)) {
    // Real anchors, not buttons. A button cannot be opened in a new tab,
    // middle-clicked, have its address copied, or be found by a screen reader's
    // list of links — all of which people genuinely do with navigation, and all
    // of which role="link" does not restore.
    const link = create('a', { href: item.path, className: 'nav-link' }, item.label);

    const exact = currentPath === item.path;
    const inSection = exact || currentPath.startsWith(item.path + '/');
    if (inSection) link.classList.add('nav-link-active');
    // Only the page you are literally on is aria-current="page". On
    // /extinguishers/ID/inspect the Extinguishers link marks the section, so it
    // gets the weaker "true" rather than claiming to be the current page.
    if (exact) link.setAttribute('aria-current', 'page');
    else if (inSection) link.setAttribute('aria-current', 'true');

    link.addEventListener('click', (e) => {
      const ev = e as MouseEvent;
      // Let the browser handle anything that means "open this elsewhere".
      if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      e.preventDefault();
      router.navigate(item.path);
    });
    nav.appendChild(link);
  }

  // Logout stays a button: it performs an action, it is not a destination.
  const logout = create('button', { className: 'nav-link nav-action', type: 'button' }, 'Logout');
  logout.addEventListener('click', onLogout);
  nav.appendChild(logout);

  return nav;
}

/** Shared styles. Wraps on narrow screens rather than overflowing sideways. */
export const APP_NAV_STYLES = `
  .nav {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }
  .nav-link {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    /* WCAG 2.2 target size: never below 24px, and comfortably tappable. */
    min-height: 32px;
    padding: 6px 10px;
    font: inherit;
    font-size: 14px;
    color: var(--text, #17130f);
    background: #fff;
    border: 1px solid var(--gray-300, #d1d5db);
    border-radius: 6px;
    text-decoration: none;
    cursor: pointer;
  }
  .nav-link:hover {
    background: var(--gray-50, #f9fafb);
  }
  .nav-link:focus-visible {
    outline: 2px solid var(--primary, #b91c1c);
    outline-offset: 2px;
  }
  .nav-link-active {
    background: var(--primary, #b91c1c);
    color: #fff;
    border-color: var(--primary, #b91c1c);
    /* Not colour alone: the active item is also bold. */
    font-weight: 700;
  }
  @media (max-width: 640px) {
    .nav { gap: 4px; }
    .nav-link { font-size: 13px; padding: 6px 8px; min-height: 32px; }
  }
`;
